import React, { useState } from 'react';
import {
  Form,
  Input,
  Col,
  TimePicker,
  Checkbox,
  Card,
  Row,
  Select,
  Upload,
  Image,
  UploadProps,
  GetProp,
} from 'antd';
import { useEffect } from 'react';
import {
  FacilityDetailType,
  FacilityDetailWithDayjsTimes,
  UserType,
} from '../../../types/types';
import styled from 'styled-components';
import { searchUsersRequest } from '../../../api/requests/users';
import { COLORS, TEXT_SIZES, FONT_WEIGHTS } from '../../../utils/constants';
import {
  CalendarOutlined,
  CloseOutlined,
  EyeOutlined,
  PlusCircleFilled,
  StarOutlined,
  EnvironmentOutlined,
} from '@ant-design/icons';
import { amenitiesOptions } from '../../../utils/utils';
import LocationPicker, { LocationValue } from '../Maps/LocationPicker';
import { Dayjs } from 'dayjs';
import { Drawer } from '../../common/Drawer';
import Button from '../../common/Button';
import ToolTipWrapper from '../../common/ToolTipWrapper';
import { FacilityStatus } from '../../../enums/enums';
import Modal from '../../common/Modal';
import { useSnackbar } from '../../../contexts/SnackbarContext/SnackbarProvider';
import { RcFile } from 'antd/es/upload';

type FileType = Parameters<GetProp<UploadProps, 'beforeUpload'>>[0];

interface FacilityModalProps {
  visible: boolean;
  onClose: () => void;
  onSubmit: (
    values: FacilityDetailType,
    imageFiles: UnifiedImage[],
    deletedAttachmentIds: number[],
  ) => Promise<void>;
  initialValues: FacilityDetailWithDayjsTimes | null;
}

export type UnifiedImage = {
  id?: number;
  url: string;
  file?: File;
  isPrimary: boolean;
  isNew: boolean;
};

const FacilityModal: React.FC<FacilityModalProps> = ({
  visible,
  onClose,
  onSubmit,
  initialValues,
}) => {
  const [form] = Form.useForm();
  const [users, setUsers] = useState<UserType[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [fileList, setFileList] = useState<any[]>([]);
  const [images, setImages] = useState<UnifiedImage[]>([]);
  const [deletedAttachmentIds, setDeletedAttachmentIds] = useState<number[]>(
    [],
  );
  const [previewIndex, setPreviewIndex] = useState<number | null>(null);
  const [locationPickerOpen, setLocationPickerOpen] = useState<boolean>(false);
  const snackbar = useSnackbar();

  const fetchUsers = async () => {
    if (visible) {
      setLoading(true);
      try {
        const response = await searchUsersRequest();
        if (response?.value?.rows) {
          setUsers(response.value.rows);
        }
      } catch (error) {
        console.error('Error fetching users:', error);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const formatTime = (value: Dayjs) =>
        value && typeof value !== 'string' ? value.format('HH:mm:ss') : value;
      const formattedValues: FacilityDetailType = {
        ...values,
        openingTime: values?.openingTime && formatTime(values.openingTime),
        closingTime: values?.closingTime && formatTime(values.closingTime),
        country: 'India',
        facilityLocation: values.facilityAddress,
        sports: initialValues?.sports || [],
      };

      await onSubmit(formattedValues, images, deletedAttachmentIds);
      setTimeout(() => {
        handleClose();
      }, 500);
    } catch (errorInfo) {
      console.warn('Validation failed:', errorInfo);
    }
  };

  const handleClose = () => {
    form.resetFields();
    setFileList([]);
    setPreviewIndex(null);
    setImages([]);
    setDeletedAttachmentIds([]);
    onClose();
  };

  const getBase64 = (img: FileType, callback: (url: string) => void) => {
    const reader = new FileReader();
    reader.addEventListener('load', () => callback(reader.result as string));
    reader.readAsDataURL(img);
  };

  const beforeUpload = (file: RcFile) => {
    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];
    const allowedExtensions = ['jpg', 'jpeg', 'png'];
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    const isValidType =
      allowedTypes.includes(file.type) &&
      fileExtension &&
      allowedExtensions.includes(fileExtension);

    if (!isValidType) {
      snackbar.error('Only jpeg, png and jpg formats allowed!');
      return Upload.LIST_IGNORE;
    }
    return false; // Prevent auto-upload but allow manual processing
  };

  const handleImageUpload: UploadProps['onChange'] = (info) => {
    // Filter out files that failed validation
    const validFiles = info.fileList.filter(
      (f) => f.status !== 'error' && f.originFileObj,
    );

    if (validFiles.length === 0) {
      return; // No valid files to process
    }

    setFileList(validFiles);
    const files = validFiles.map((f) => f.originFileObj as FileType);
    setLoading(true);

    Promise.all(
      files.map(
        (file) =>
          new Promise<string>((resolve) => {
            getBase64(file, (url) => resolve(url));
          }),
      ),
    ).then((urls) => {
      const newImages: UnifiedImage[] = urls.map((url, idx) => ({
        url,
        file: files[idx],
        isPrimary: false,
        isNew: true,
      }));
      setImages((prev) => [...prev.filter((img) => !img.isNew), ...newImages]);
      setLoading(false);
    });
  };

  const handleRemoveImage = (index: number) => {
    setImages((prev) => {
      const img = prev[index];

      if (!img.isNew && img.id) {
        setDeletedAttachmentIds((prevIds) => [...prevIds, img.id!]);
      }

      return prev.filter((_, i) => i !== index);
    });
    setFileList((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSetPrimary = (index: number) => {
    setImages((prev) =>
      prev.map((img, i) => ({
        ...img,
        isPrimary: i === index && !img.isPrimary,
      })),
    );
  };

  const handleLocationChange = (location: LocationValue) => {
    const address = location.address;
    if (address) {
      form.setFieldsValue({
        facilityAddress: address.street || '',
        city: address.city || '',
        state: address.state || '',
        zipCode: address.pincode || '',
      });
    }

    form.setFieldsValue({
      facilityLatitude: location.lat,
      facilityLongitude: location.lng,
    });
    setLocationPickerOpen(false);
  };

  const handleOwnerChange = (userId: number) => {
    const selectedUser = users.find((u) => u.userId === userId);

    if (selectedUser) {
      form.setFieldsValue({
        facilityContactName: selectedUser.fullName || '',
        facilityContactEmail: selectedUser.email || '',
        facilityContactPhone: selectedUser.phoneNumber || '',
      });
    }
  };

  useEffect(() => {
    if (visible) {
      fetchUsers();
    }
  }, [visible]);

  useEffect(() => {
    if (visible && initialValues) {
      const cleanInitialValues = { ...initialValues };

      if (
        cleanInitialValues.openingTime &&
        !cleanInitialValues.openingTime.isValid()
      ) {
        cleanInitialValues.openingTime = undefined;
      }
      if (
        cleanInitialValues.closingTime &&
        !cleanInitialValues.closingTime.isValid()
      ) {
        cleanInitialValues.closingTime = undefined;
      }
      form.setFieldsValue(cleanInitialValues);
      if (initialValues.attachments) {
        setImages(
          initialValues.attachments.map((att) => ({
            id: att.attachmentId,
            url: att.attachmentUrl,
            isPrimary: att.isPrimary,
            isNew: false,
          })),
        );
      }
    }
  }, [visible, initialValues, form]);

  useEffect(() => {
    return () => {
      form.resetFields();
    };
  }, []);

  return (
    <Drawer
      title={initialValues ? 'Edit Facility' : 'Add New Facility'}
      open={visible}
      onClose={handleClose}
      destroyOnHidden
      width={'40vw'}
      footer={[
        <FooterRow key="form-actions">
          <Button key="cancel" onClick={handleClose}>
            Cancel
          </Button>
          <Button key="submit" type="primary" onClick={handleSubmit}>
            {initialValues ? 'Update' : 'Create'} Facility
          </Button>
        </FooterRow>,
      ]}
    >
      <StyledCol>
        <Form form={form} layout="vertical">
          <FormSection>
            <SectionTitle>
              <CalendarOutlined /> Basic Information
            </SectionTitle>

            <StyledFormItem
              name="facilityName"
              label="Facility Name"
              rules={[
                { required: true, message: 'Please enter facility name' },
              ]}
            >
              <StyledInput placeholder="Enter facility name" size="large" />
            </StyledFormItem>
            <StyledFormItem
              name="facilityDescription"
              label="Description"
              rules={[
                {
                  required: true,
                  message: 'Please enter description',
                },
              ]}
            >
              <StyledInput
                size="large"
                placeholder="Brief description of the facility and its features"
              />
            </StyledFormItem>
            <StyledFormItem
              name="facilityStatus"
              label="Status"
              rules={[
                {
                  required: true,
                  message: 'Please select status',
                },
              ]}
            >
              <StyledSelect
                placeholder="Select status"
                size="large"
                loading={loading}
                showSearch
                optionFilterProp="children"
              >
                {Object.entries(FacilityStatus)
                  .filter(([key, value]) => isNaN(Number(key)))
                  .map(([key, value]) => (
                    <StyledSelect.Option key={value} value={value}>
                      {key}
                    </StyledSelect.Option>
                  ))}
              </StyledSelect>
            </StyledFormItem>
            <StyledFormItem name="facilityImage" label="Upload Images">
              <ImageUploadContainer>
                <StyledUpload
                  fileList={fileList}
                  onChange={handleImageUpload}
                  beforeUpload={beforeUpload}
                  accept="image/jpeg,image/png,image/jpg"
                  showUploadList={false}
                  listType="picture-card"
                >
                  <UploadButton>
                    <StyledPlusCircleFilled />
                    <StyledUploadContent>
                      Add Facility Image
                    </StyledUploadContent>
                  </UploadButton>
                </StyledUpload>
                {images.map((img, index) => (
                  <ImagePreviewContainer key={index} primary={img.isPrimary}>
                    <RemoveIcon onClick={() => handleRemoveImage(index)}>
                      <ToolTipWrapper title="Remove">
                        <CloseOutlined />
                      </ToolTipWrapper>
                    </RemoveIcon>
                    <StyledImage
                      src={img.url}
                      alt={`Facility image ${index + 1}`}
                      width={120}
                      height={120}
                      preview={false}
                    />
                    <HoverIcons className="hover-icons">
                      <ToolTipWrapper title="Preview">
                        <IconButton onClick={() => setPreviewIndex(index)}>
                          <EyeOutlined />
                        </IconButton>
                      </ToolTipWrapper>
                      <ToolTipWrapper
                        title={
                          img.isPrimary ? 'Remove as Primary' : 'Set as Primary'
                        }
                      >
                        <IconButton onClick={() => handleSetPrimary(index)}>
                          <StarOutlined />
                        </IconButton>
                      </ToolTipWrapper>
                    </HoverIcons>
                  </ImagePreviewContainer>
                ))}
                {previewIndex !== null && (
                  <Image
                    src={images[previewIndex].url}
                    style={{ display: 'none' }}
                    preview={{
                      visible: true,
                      onVisibleChange: (vis) => {
                        if (!vis) setPreviewIndex(null);
                      },
                    }}
                  />
                )}
              </ImageUploadContainer>
              <SupportedFormatsText>
                Supported formats: JPG, JPEG, PNG
              </SupportedFormatsText>
            </StyledFormItem>
          </FormSection>
          <FormSection>
            <SectionTitleWithButton>
              <SectionTitle>
                <CalendarOutlined /> Location Information
              </SectionTitle>
              <LocationPickerButton
                type="primary"
                icon={<EnvironmentOutlined />}
                onClick={() => setLocationPickerOpen(true)}
                size="middle"
              >
                Pick Location
              </LocationPickerButton>
            </SectionTitleWithButton>
            <StyledFormItem
              name="facilityAddress"
              label="Street Address"
              rules={[{ required: true, message: ' Please enter address' }]}
            >
              <StyledInput
                placeholder="123 Sports Avenue or use location picker"
                size="large"
              />
            </StyledFormItem>
            <StyledFormItem name="facilityLatitude" hidden>
              <Input type="hidden" />
            </StyledFormItem>
            <StyledFormItem name="facilityLongitude" hidden>
              <Input type="hidden" />
            </StyledFormItem>
            <StyledRow>
              <StyledFormItem
                name="city"
                label="City"
                rules={[{ required: true, message: ' Please enter city' }]}
              >
                <StyledInput placeholder="Enter city" size="large" />
              </StyledFormItem>
              <StyledFormItem
                name="state"
                label="State"
                rules={[{ required: true, message: ' Please enter state' }]}
              >
                <StyledInput placeholder="Enter state" size="large" />
              </StyledFormItem>
              <StyledFormItem
                name="zipCode"
                label="Pincode"
                rules={[{ required: true, message: ' Please enter pin code' }]}
              >
                <StyledInput placeholder="Enter pin code" size="large" />
              </StyledFormItem>
            </StyledRow>
          </FormSection>
          <FormSection>
            <SectionTitle>
              <CalendarOutlined /> Contact Information
            </SectionTitle>
            <StyledRow>
              <StyledFormItem
                name="facilityOwnerId"
                label="Owner"
                rules={[
                  {
                    required: true,
                    message: 'Please select an owner',
                  },
                ]}
              >
                <StyledSelect
                  placeholder="Select owner"
                  size="large"
                  loading={loading}
                  showSearch
                  optionFilterProp="children"
                  onChange={(value) => handleOwnerChange(value as number)}
                >
                  {users?.map((user) => (
                    <Select.Option key={user.userId} value={user.userId}>
                      {user.fullName || user.email || `User ${user.userId}`}
                    </Select.Option>
                  ))}
                </StyledSelect>
              </StyledFormItem>
              <StyledFormItem
                name="facilityContactName"
                label="Contact Person Name"
                rules={[
                  {
                    required: true,
                    message: ' Please enter contact person name',
                  },
                ]}
              >
                <StyledInput
                  placeholder="Enter contact person name"
                  size="large"
                />
              </StyledFormItem>
            </StyledRow>
            <StyledRow>
              <StyledFormItem
                name="facilityContactEmail"
                label="Email ID"
                rules={[
                  {
                    required: true,
                    message: 'Please enter email',
                  },
                  {
                    type: 'email',
                    message: ' Please enter a valid email address',
                  },
                ]}
                validateTrigger="onSubmit"
              >
                <StyledInput placeholder="Enter email ID" size="large" />
              </StyledFormItem>
              <StyledFormItem
                name="facilityContactPhone"
                label="Phone Number"
                rules={[
                  {
                    required: true,
                    message: 'Please enter contact number',
                  },
                  {
                    pattern: /^[0-9]+$/,
                    message: 'Phone number must contain only digits',
                  },
                  {
                    len: 10,
                    message: 'Phone number must be 10 digits',
                  },
                ]}
                validateTrigger="onSubmit"
              >
                <StyledInput
                  placeholder="Enter phone number"
                  size="large"
                  maxLength={10}
                  onKeyDown={(e) => {
                    if (!/[0-9]/.test(e.key) && e.key !== 'Backspace') {
                      e.preventDefault();
                    }
                  }}
                />
              </StyledFormItem>
            </StyledRow>
          </FormSection>
          <FormSection>
            <SectionTitle>
              <CalendarOutlined /> Operating Hours
            </SectionTitle>
            <StyledRow>
              <StyledFormItem name="openingTime" label="Opening Time">
                <StyledTimePicker format="hh:mm A" use12Hours size="large" />
              </StyledFormItem>
              <StyledFormItem
                name="closingTime"
                label="Closing Time"
                dependencies={['openingTime']}
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const openingTime = getFieldValue('openingTime');
                      if (!openingTime || !value) {
                        return Promise.resolve();
                      }
                      if (value.isBefore(openingTime)) {
                        return Promise.reject(
                          new Error(
                            'Closing time cannot be earlier than opening time',
                          ),
                        );
                      }
                      if (value.isSame(openingTime)) {
                        return Promise.reject(
                          new Error(
                            'Closing time cannot be same as opening time',
                          ),
                        );
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <StyledTimePicker format="hh:mm A" use12Hours size="large" />
              </StyledFormItem>
            </StyledRow>
          </FormSection>
          <FormSection>
            <SectionTitle>
              <CalendarOutlined /> Amenities & Features
            </SectionTitle>
            <Form.Item name="facilityAmenities">
              <Checkbox.Group style={{ width: '100%' }}>
                <StyledCheckboxRow>
                  {amenitiesOptions?.map((option) => (
                    <Checkbox
                      key={option.value}
                      value={option.value}
                      defaultChecked={initialValues?.facilityAmenities?.includes(
                        option.value,
                      )}
                    >
                      {option.label}
                    </Checkbox>
                  ))}
                </StyledCheckboxRow>
              </Checkbox.Group>
            </Form.Item>
          </FormSection>
        </Form>
      </StyledCol>

      <Modal
        title="Pick Facility Location"
        open={locationPickerOpen}
        onCancel={() => setLocationPickerOpen(false)}
        footer={null}
        width={1200}
      >
        <LocationPicker
          value={
            form.getFieldValue('facilityLatitude') &&
            form.getFieldValue('facilityLongitude')
              ? {
                  lat: form.getFieldValue('facilityLatitude'),
                  lng: form.getFieldValue('facilityLongitude'),
                  address: undefined,
                }
              : undefined
          }
          onLocationChange={handleLocationChange}
          height="500px"
        />
      </Modal>
    </Drawer>
  );
};

export const StyledCol = styled(Col)`
  height: 100%;
  overflow-y: auto;
  padding: 16px 24px;
`;

export const FormSection = styled(Card)`
  margin-bottom: 24px;
  border-radius: 16px;
`;

export const SectionTitle = styled.h3`
  margin-bottom: 16px;
  font-size: ${TEXT_SIZES.MD};
  font-weight: ${FONT_WEIGHTS.SEMIBOLD};
`;

const SectionTitleWithButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const LocationPickerButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const StyledCheckboxRow = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px 16px;
  align-items: center;
`;

const StyledInput = styled(Input)`
  height: 43px;
`;

export const StyledFormItem = styled(Form.Item)`
  font-weight: ${FONT_WEIGHTS.MEDIUM};
  flex: 1;
`;

const StyledRow = styled(Row)`
  gap: 16px;
  width: 100%;
`;

export const FooterRow = styled(Row)`
  width: 100%;
  justify-content: end;
  gap: 16px;
`;

const ImageUploadContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  width: 92%;
  align-items: center;
  justify-content: center;
`;

const ImagePreviewContainer = styled.div<{ primary?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  position: relative;
  padding: 4px;
  border-radius: 4px;
  border: ${({ primary }) => primary && `3px solid ${COLORS.skyBlue.primary}`};
  &:hover .hover-icons {
    opacity: 1;
  }
`;

const StyledImage = styled(Image)`
  border-radius: 4px;
  object-fit: cover;
`;

const StyledUpload = styled(Upload)`
  .ant-upload {
    border-radius: 8px;
    background-color: ${COLORS.grey.extraLight};
    border: none !important;
    transition: all 0.3s ease;
    width: 130px !important;
    height: 130px !important;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const UploadButton = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px 32px;
  color: ${COLORS.grey.secondary};
  font-weight: ${FONT_WEIGHTS.MEDIUM};
  cursor: pointer;
`;

const StyledSelect = styled(Select)`
  width: 100%;
  .ant-select-selector {
    height: 43px !important;
  }
`;

const StyledTimePicker = styled(TimePicker)`
  width: 100%;
  height: 43px;
`;

const RemoveIcon = styled.div`
  position: absolute;
  top: 6px;
  right: 14px;
  color: ${COLORS.white.primary};
  border: 2px solid ${COLORS.white.primary};
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  font-size: 14px;
`;

const HoverIcons = styled.div`
  position: absolute;
  display: flex;
  gap: 4px;
  opacity: 0;
  transition: opacity 0.2s ease;
  margin-top: 2px;
  margin-right: 8px;
`;

const IconButton = styled.div`
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: ${COLORS.black.background};
  color: ${COLORS.white.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 14px;

  &:hover {
    background: ${COLORS.skyBlue.primary};
  }
`;

const StyledPlusCircleFilled = styled(PlusCircleFilled)`
  font-size: 24px;
  color: ${COLORS.grey.dark};
`;

const StyledUploadContent = styled.div`
  margin-top: 8px;
  white-space: nowrap;
  color: ${COLORS.grey.primary};
  font-size: 12px;
`;

const SupportedFormatsText = styled.p`
  margin: 4px 0px;
  font-size: ${TEXT_SIZES.XS}px;
  color: ${COLORS.grey.secondary};
`;

export default FacilityModal;
